<script setup>
/**
 * The chat side of tracker linking, in the conversation Details sidebar: which
 * tracker tickets this conversation is filed under, plus the picker that files
 * it under another one. The write always goes to the tracker's own
 * ticket_links endpoint - linking never touches this conversation's status.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import ConversationApi from 'dashboard/api/inbox/conversation';

import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import StatusPill from 'dashboard/components-next/Conversation/StatusPill.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const router = useRouter();

const links = ref([]);
const trackers = ref([]);
const isLinking = ref(false);

const linkedTicketIds = computed(() => links.value.map(link => link.ticket.id));

const trackerOptions = computed(() =>
  trackers.value
    .filter(tracker => !linkedTicketIds.value.includes(tracker.id))
    .map(tracker => ({
      value: tracker.id,
      label: `#${tracker.id} · ${tracker.subject || t('TICKETS.EMPTY.NO_SUBJECT')}`,
    }))
);

const fetchLinks = async () => {
  try {
    const { data } = await ConversationApi.getTicketLinks(props.conversationId);
    links.value = data.payload.tracker_tickets;
  } catch (error) {
    useAlert(t('TICKETS.DETAIL.LINKED.FETCH_ERROR'));
  }
};

const fetchTrackers = async () => {
  try {
    const { data } = await ConversationApi.getTrackerTickets();
    trackers.value = data.data.payload;
  } catch (error) {
    // The picker degrades to empty; the linked list above still renders.
    trackers.value = [];
  }
};

const linkTo = async ticketId => {
  if (!ticketId || isLinking.value) return;
  isLinking.value = true;
  try {
    await ConversationApi.createTicketLink({
      ticketId,
      linkedConversationId: props.conversationId,
    });
    await fetchLinks();
    useAlert(t('TICKETS.DETAIL.TRACKERS.LINK_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('TICKETS.DETAIL.TRACKERS.LINK_ERROR')
    );
  } finally {
    isLinking.value = false;
  }
};

const unlink = async link => {
  try {
    await ConversationApi.deleteTicketLink({
      ticketId: link.ticket.id,
      linkId: link.id,
    });
    links.value = links.value.filter(({ id }) => id !== link.id);
    useAlert(t('TICKETS.DETAIL.LINKED.UNLINK_SUCCESS'));
  } catch (error) {
    useAlert(t('TICKETS.DETAIL.LINKED.UNLINK_ERROR'));
  }
};

const openTicket = link =>
  router.push({
    name: 'tickets_conversation',
    params: { conversationId: link.ticket.id },
  });

watch(
  () => props.conversationId,
  () => fetchLinks()
);
onMounted(() => {
  fetchLinks();
  fetchTrackers();
});
</script>

<template>
  <div class="flex flex-col gap-1.5 px-4 py-3 border-b border-n-weak">
    <h4 class="text-xs font-medium tracking-wide uppercase text-n-slate-10">
      {{ t('TICKETS.DETAIL.TRACKERS.TITLE') }}
    </h4>

    <div
      v-for="link in links"
      :key="link.id"
      class="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-n-alpha-1"
    >
      <button
        type="button"
        class="flex items-center flex-1 min-w-0 gap-2 text-start"
        :data-testid="`tracker-ticket-${link.ticket.id}`"
        @click="openTicket(link)"
      >
        <span class="text-xs shrink-0 text-n-slate-10">
          #{{ link.ticket.id }}
        </span>
        <span class="text-xs font-medium truncate text-n-slate-12">
          {{ link.ticket.subject || t('TICKETS.EMPTY.NO_SUBJECT') }}
        </span>
      </button>
      <StatusPill :state="link.ticket.ticket_state" icon-only />
      <button
        type="button"
        :data-testid="`unlink-tracker-${link.ticket.id}`"
        :title="t('TICKETS.DETAIL.LINKED.UNLINK')"
        :aria-label="t('TICKETS.DETAIL.LINKED.UNLINK')"
        class="grid rounded size-5 shrink-0 place-items-center text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-ruby-11"
        @click="unlink(link)"
      >
        <span class="i-lucide-unlink size-3.5" />
      </button>
    </div>

    <ComboBox
      model-value=""
      data-testid="tracker-link-picker"
      :options="trackerOptions"
      :disabled="isLinking"
      :placeholder="t('TICKETS.DETAIL.TRACKERS.LINK_PLACEHOLDER')"
      :empty-state="t('TICKETS.DETAIL.TRACKERS.EMPTY')"
      @update:model-value="linkTo"
    />
  </div>
</template>
